import { useCallback, useMemo, useState } from "react";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  List,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { EVOTRUX_BORDER_GREY } from "constants/index";

import { CheckableRow } from "./CheckableRow";
import { TeammateSection } from "./TeammateSection";
import {
  getSelectAllState,
  listSelectAllRoleIds,
  listTeammateGroups,
  searchTeammates,
} from "./utils";

interface Props {
  teammates: CompanyMember[];
  callerBranchId: number | null;
  sharedRoleIds: number[];
  sharerRoleId: number | null;
  sharing: boolean;
  error: string | null;
  closeTooltip: string;
  onShare: (recipientRoleIds: number[], onSuccess: VoidFunction) => void;
  onClose: VoidFunction;
}

export function TeammatePicker(props: Props) {
  const {
    teammates,
    callerBranchId,
    sharedRoleIds,
    sharerRoleId,
    sharing,
    error,
    closeTooltip,
    onShare,
    onClose,
  } = props;
  const [query, setQuery] = useState<string>("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(
    new Set(),
  );
  // Fixed when the picker mounts so that rows keep their place once a share lands.
  // eslint-disable-next-line react/hook-use-state
  const [sharedRoleIdsAtOpen] = useState<Set<number>>(
    () => new Set(sharedRoleIds),
  );
  const sharedRoleIdSet = useMemo(
    () => new Set(sharedRoleIds),
    [sharedRoleIds],
  );
  // The sharer already has the template, so select all skips them too. They can
  // still be ticked by hand.
  const alreadyHaveTemplateRoleIds = useMemo(
    () =>
      new Set(
        sharerRoleId === null
          ? sharedRoleIds
          : [...sharedRoleIds, sharerRoleId],
      ),
    [sharedRoleIds, sharerRoleId],
  );
  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery !== "";

  const matches = useMemo(
    () => searchTeammates(teammates, query),
    [teammates, query],
  );
  const groups = useMemo(
    () =>
      listTeammateGroups({
        matches,
        callerBranchId,
        isSearching,
        sharedRoleIdsAtOpen,
        sharerRoleId,
      }),
    [matches, callerBranchId, isSearching, sharedRoleIdsAtOpen, sharerRoleId],
  );

  const selectAllRoleIds = useMemo(
    () => listSelectAllRoleIds(matches, alreadyHaveTemplateRoleIds),
    [matches, alreadyHaveTemplateRoleIds],
  );
  const { allSelected, someSelected } = getSelectAllState(
    selectAllRoleIds,
    selectedRoleIds,
  );
  const selectAllLabel = isSearching ? "Select all results" : "Select all";

  // Stable so that memoized rows only re-render when their own state changes.
  const toggle = useCallback((roleId: number) => {
    setSelectedRoleIds((previous) => {
      const next = new Set(previous);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }, []);

  const toggleAll = () => {
    setSelectedRoleIds((previous) => {
      const next = new Set(previous);
      selectAllRoleIds.forEach((roleId) =>
        allSelected ? next.delete(roleId) : next.add(roleId),
      );
      return next;
    });
  };

  const clearSelection = () => setSelectedRoleIds(new Set());

  const selectedCount = selectedRoleIds.size;

  return (
    <>
      <Stack px={3} pb={1} spacing={1} flexShrink={0}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search teammates by name or branch..."
          slotProps={{
            htmlInput: { "aria-label": "Search teammates" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <Tooltip
                    describeChild
                    title="Clear the search and show every teammate again"
                  >
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setQuery("")}
                    >
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        {selectAllRoleIds.length > 0 && (
          <List disablePadding>
            <CheckableRow
              checked={allSelected}
              indeterminate={someSelected}
              ariaLabel={selectAllLabel}
              onClick={toggleAll}
            >
              <Typography variant="body2" fontWeight="fontWeightBold">
                {selectAllLabel}{" "}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  ({selectAllRoleIds.length})
                </Typography>
              </Typography>
            </CheckableRow>
          </List>
        )}
      </Stack>
      <DialogContent sx={{ pt: 0 }}>
        {groups.length === 0 && (
          <Stack alignItems="center" textAlign="center" py={4}>
            <PersonSearchOutlinedIcon color="disabled" fontSize="large" />
            <Typography variant="body2" mt={1}>
              {isSearching
                ? `No teammates match “${trimmedQuery}”`
                : "You have no teammates to share with."}
            </Typography>
            {isSearching && (
              <Typography variant="caption" color="text.secondary">
                Try a different name or branch.
              </Typography>
            )}
          </Stack>
        )}
        <Stack spacing={1.5}>
          {groups.map((group) => (
            <TeammateSection
              key={group.label}
              label={group.label}
              teammates={group.teammates}
              callerBranchId={callerBranchId}
              sharedRoleIds={sharedRoleIdSet}
              sharerRoleId={sharerRoleId}
              selectedRoleIds={selectedRoleIds}
              onToggle={toggle}
            />
          ))}
        </Stack>
      </DialogContent>
      <ErrorTypography error={error} />
      <Divider sx={{ borderColor: EVOTRUX_BORDER_GREY }} />
      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {selectedCount > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {selectedCount} selected
              </Typography>
              <Tooltip describeChild title="Untick every selected teammate">
                <Button
                  onClick={clearSelection}
                  sx={{
                    typography: "body2",
                    fontWeight: "fontWeightMedium",
                    textTransform: "none",
                    minWidth: "auto",
                    p: 0,
                    "&:hover, &.Mui-focusVisible": {
                      textDecoration: "underline",
                      bgcolor: "transparent",
                    },
                  }}
                >
                  Clear
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip describeChild title={closeTooltip}>
            <Button onClick={onClose} sx={{ color: "text.secondary" }}>
              Close
            </Button>
          </Tooltip>
          <Tooltip
            describeChild
            title={
              selectedCount === 0
                ? "Select at least one teammate to share this template with"
                : "Give each selected teammate their own copy of this template"
            }
          >
            <span>
              <Button
                variant="contained"
                disabled={selectedCount === 0 || sharing}
                onClick={() =>
                  onShare(Array.from(selectedRoleIds), clearSelection)
                }
              >
                {selectedCount === 0
                  ? "Share"
                  : `Share with ${selectedCount} ${selectedCount === 1 ? "person" : "people"}`}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </>
  );
}
